import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  EyeOutlined,
  FileOutlined,
} from "@ant-design/icons";
import {
  Button,
  Descriptions,
  Divider,
  Dropdown,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Typography,
} from "antd";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { getFilePath } from "utils/common";
import RejectModal from "./components/RejectModal";
import * as restaurantContractActions from "./slice";

const { Paragraph } = Typography;

function RestaurantContract() {
  const dispatch = useDispatch();
  const {
    restaurantContracts,
    pagination,
    isLoading,
    isProcessing,
    isCompleted,
  } = useSelector((state) => state.restaurantContract);

  const [openRejectModal, setOpenRejectModal] = useState(false);
  const [openDetailModal, setOpenDetailModal] = useState(false);
  const [selectingContract, setSelectingContract] = useState(null);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterRestaurant, setFilterRestaurant] = useState("");

  useEffect(() => {
    dispatch(restaurantContractActions.fetchRestaurantContracts(pagination));
  }, []);

  useEffect(() => {
    if (isCompleted) {
      setOpenRejectModal(false);
      setSelectingContract(null);
    }
  }, [isCompleted]);

  const onVerify = (contract) => {
    Modal.confirm({
      title: "Xác nhận xác minh hợp đồng",
      content: `Bạn có chắc chắn muốn xác minh hợp đồng này?`,
      onOk: () => {
        dispatch(
          restaurantContractActions.verifyRestaurantContract(contract._id)
        );
      },
      centered: true,
    });
  };

  const onReject = (contract) => {
    setSelectingContract(contract);
    setOpenRejectModal(true);
  };

  const onDelete = (contract) => {
    Modal.confirm({
      title: "Xác nhận xóa hợp đồng",
      content: `Bạn có chắc chắn muốn xóa hợp đồng này?`,
      onOk: () => {
        dispatch(
          restaurantContractActions.deleteRestaurantContract(contract._id)
        );
      },
      centered: true,
    });
  };

  const onCloseRejectModal = () => {
    setOpenRejectModal(false);
    setSelectingContract(null);
  };

  const onViewDetail = (contract) => {
    setSelectingContract(contract);
    setOpenDetailModal(true);
  };

  const onCloseDetailModal = () => {
    setOpenDetailModal(false);
    setSelectingContract(null);
  };

  const onSubmitReject = (reason) => {
    if (!reason || !reason.trim()) {
      toast.error("Vui lòng nhập lý do từ chối");
      return;
    }
    if (selectingContract?._id) {
      dispatch(
        restaurantContractActions.rejectRestaurantContract(
          selectingContract._id,
          reason
        )
      );
    }
    setOpenRejectModal(false);
  };

  const onChangeSearch = (e) => {
    setSearch(e.target.value);
  };

  const buildParams = (paginationOverride = {}) => {
    const params = {
      ...pagination,
      ...paginationOverride,
    };
    if (
      filterStatus &&
      filterStatus !== null &&
      filterStatus !== undefined &&
      filterStatus !== ""
    ) {
      params.status = filterStatus;
    }
    if (search && search.trim()) {
      params.search = search.trim();
    }
    if (filterRestaurant && filterRestaurant.trim()) {
      params.restaurant = filterRestaurant.trim();
    }
    return params;
  };

  const onChangeStatusFilter = (value) => {
    const newStatus = value === undefined || value === "" ? null : value;
    setFilterStatus(newStatus);
    const params = {
      ...pagination,
      current: 1,
    };
    if (
      newStatus &&
      newStatus !== null &&
      newStatus !== undefined &&
      newStatus !== ""
    ) {
      params.status = newStatus;
    }
    if (search && search.trim()) {
      params.search = search.trim();
    }
    if (filterRestaurant && filterRestaurant.trim()) {
      params.restaurant = filterRestaurant.trim();
    }
    dispatch(restaurantContractActions.fetchRestaurantContracts(params));
  };

  const onChangeRestaurantFilter = (e) => {
    setFilterRestaurant(e.target.value);
  };

  const onSearch = () => {
    dispatch(
      restaurantContractActions.fetchRestaurantContracts(
        buildParams({ current: 1 })
      )
    );
  };

  const onTableChange = async (pagination) => {
    dispatch(
      restaurantContractActions.fetchRestaurantContracts(
        buildParams(pagination)
      )
    );
  };

  const onClearFilters = () => {
    setSearch("");
    setFilterStatus(null);
    setFilterRestaurant("");
    dispatch(
      restaurantContractActions.fetchRestaurantContracts({
        ...pagination,
        current: 1,
      })
    );
  };

  const getStatusTag = (status) => {
    switch (status) {
      case "verified":
        return <Tag color="green">Đã xác minh</Tag>;
      case "rejected":
        return <Tag color="red">Đã từ chối</Tag>;
      case "pending":
        return <Tag color="orange">Chờ xử lý</Tag>;
      case "draft":
      default:
        return <Tag color="default">Bản nháp</Tag>;
    }
  };

  const columns = [
    {
      title: "STT",
      align: "center",
      width: "5%",
      render: (text, record, index) => (
        <Typography.Paragraph
          copyable={{
            text: record._id,
            tooltips: ["Sao chép ID", "Đã sao chép!"],
          }}
        >
          {(pagination.current - 1) * pagination.pageSize + index + 1}
        </Typography.Paragraph>
      ),
    },
    {
      title: "Quán ăn",
      dataIndex: "restaurant",
      width: "15%",
      render: (restaurant) => (
        <Paragraph
          copyable={{
            text: restaurant?._id,
            tooltips: ["Sao chép ID", "Đã sao chép!"],
          }}
          style={{ margin: 0 }}
        >
          {restaurant?.name || "N/A"}
        </Paragraph>
      ),
    },
    {
      title: "Mã hợp đồng",
      dataIndex: "code",
      width: "12%",
      render: (code, record) => (
        <div>
          <div>{code || "N/A"}</div>
          {record.isUpdated && (
            <Tag color="blue" bordered={false} style={{ marginTop: 4 }}>
              Admin upload
            </Tag>
          )}
        </div>
      ),
    },
    {
      title: "Trạng thái",
      dataIndex: "status",
      width: "15%",
      align: "center",
      render: (status, record) => (
        <div style={{ textAlign: "center" }}>
          <div>{getStatusTag(status)}</div>
          {status === "rejected" && record.rejectReason && (
            <div style={{ marginTop: 8, textAlign: "left", maxWidth: 200 }}>
              <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>
                Lý do từ chối:
              </div>
              <Paragraph
                ellipsis={{ rows: 2, expandable: true }}
                style={{ margin: 0, fontSize: 12 }}
              >
                {record.rejectReason}
              </Paragraph>
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Ngày ký",
      dataIndex: "signedAt",
      width: "12%",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY HH:mm") : "N/A"),
    },
    {
      title: "Ngày xử lý",
      dataIndex: "processedAt",
      width: "12%",
      render: (date) => (date ? dayjs(date).format("DD/MM/YYYY HH:mm") : "N/A"),
    },
    {
      title: "Thao tác",
      align: "center",
      width: "10%",
      fixed: "right",
      render: (record) => {
        const KEY_ACTIONS = {
          VIEW_DETAIL: "view_detail",
          VERIFY: "verify",
          REJECT: "reject",
          DELETE: "delete",
        };

        const onClick = ({ key }) => {
          switch (key) {
            case KEY_ACTIONS.VIEW_DETAIL:
              onViewDetail(record);
              break;
            case KEY_ACTIONS.VERIFY:
              onVerify(record);
              break;
            case KEY_ACTIONS.REJECT:
              onReject(record);
              break;
            case KEY_ACTIONS.DELETE:
              onDelete(record);
              break;
            default:
              break;
          }
        };

        const menuItems = [
          {
            key: KEY_ACTIONS.VIEW_DETAIL,
            label: "Xem chi tiết",
            icon: <EyeOutlined />,
          },
          ...(record.status === "pending"
            ? [
                {
                  key: KEY_ACTIONS.VERIFY,
                  label: "Xác minh",
                  icon: <CheckOutlined />,
                },
                {
                  key: KEY_ACTIONS.REJECT,
                  label: "Từ chối",
                  icon: <CloseOutlined />,
                  danger: true,
                },
              ]
            : []),
          {
            key: KEY_ACTIONS.DELETE,
            label: "Xóa",
            icon: <DeleteOutlined />,
            danger: true,
          },
        ];

        return (
          <Dropdown
            menu={{ items: menuItems, onClick }}
            disabled={isProcessing}
          >
            <Button disabled={isProcessing}>
              <Space>
                Thao tác
                <DownOutlined />
              </Space>
            </Button>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <Row justify="space-between" align="middle">
        <Typography.Title level={2}>
          [{pagination?.total || "0"}] Quản lý hợp đồng
        </Typography.Title>
        <Space size="middle" wrap>
          <Select
            placeholder="Lọc theo trạng thái"
            value={filterStatus || ""}
            onChange={onChangeStatusFilter}
            allowClear
            style={{ width: 180 }}
          >
            <Select.Option value="">Tất cả</Select.Option>
            <Select.Option value="draft">Bản nháp</Select.Option>
            <Select.Option value="pending">Chờ xử lý</Select.Option>
            <Select.Option value="verified">Đã xác minh</Select.Option>
            <Select.Option value="rejected">Đã từ chối</Select.Option>
          </Select>
          <Input
            placeholder="Tìm theo ID quán"
            value={filterRestaurant}
            onChange={onChangeRestaurantFilter}
            style={{ width: 200 }}
            allowClear
          />
          <Input.Search
            placeholder="Tìm theo mã hợp đồng hoặc tên quán"
            value={search}
            onChange={onChangeSearch}
            onSearch={onSearch}
            enterButton
            allowClear
            style={{ width: 250 }}
          />
        </Space>
      </Row>
      <Divider />
      <Table
        bordered
        loading={isLoading}
        columns={columns}
        dataSource={restaurantContracts}
        rowKey={(record) => record._id}
        pagination={pagination}
        onChange={onTableChange}
        scroll={{ x: 1200 }}
      />
      {openRejectModal && (
        <RejectModal
          open={openRejectModal}
          isProcessing={isProcessing}
          onSubmit={onSubmitReject}
          onCancel={onCloseRejectModal}
        />
      )}
      {openDetailModal && selectingContract && (
        <Modal
          title="Chi tiết hợp đồng"
          open={openDetailModal}
          onCancel={onCloseDetailModal}
          footer={[
            <Button key="close" onClick={onCloseDetailModal}>
              Đóng
            </Button>,
          ]}
          width={1000}
          centered
        >
          <Descriptions bordered column={2} style={{ marginBottom: 16 }}>
            <Descriptions.Item label="Quán ăn" span={2}>
              <Paragraph
                copyable={{
                  text: selectingContract.restaurant?._id,
                  tooltips: ["Sao chép ID", "Đã sao chép!"],
                }}
                style={{ margin: 0 }}
              >
                {selectingContract.restaurant?.name || "N/A"}
              </Paragraph>
            </Descriptions.Item>
            <Descriptions.Item label="Mã hợp đồng">
              {selectingContract.code || "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Trạng thái">
              {getStatusTag(selectingContract.status)}
            </Descriptions.Item>
            <Descriptions.Item label="Ngày ký" span={2}>
              {selectingContract.signedAt
                ? dayjs(selectingContract.signedAt).format("DD/MM/YYYY HH:mm")
                : "N/A"}
            </Descriptions.Item>
            <Descriptions.Item label="Ngày xử lý" span={2}>
              {selectingContract.processedAt
                ? dayjs(selectingContract.processedAt).format(
                    "DD/MM/YYYY HH:mm"
                  )
                : "N/A"}
            </Descriptions.Item>
            {selectingContract.status === "rejected" &&
              selectingContract.rejectReason && (
                <Descriptions.Item label="Lý do từ chối" span={2}>
                  {selectingContract.rejectReason}
                </Descriptions.Item>
              )}
          </Descriptions>
          {selectingContract.file && (
            <div>
              <div style={{ marginBottom: 8 }}>
                <Space size="large">
                  <a
                    href={getFilePath(selectingContract.file)}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    <FileOutlined /> Tải xuống file hợp đồng
                  </a>
                  <a
                    href={getFilePath(
                      selectingContract.file.replace(
                        "uploads",
                        "uploads/preview"
                      )
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <EyeOutlined /> Xem trong tab mới
                  </a>
                </Space>
              </div>
              <object
                data={`${getFilePath(
                  selectingContract.file.replace("uploads", "uploads/preview")
                )}#toolbar=0`}
                type="application/pdf"
                style={{
                  width: "100%",
                  height: "600px",
                  border: "1px solid #d9d9d9",
                  borderRadius: "4px",
                }}
              >
                <div style={{ padding: 20, textAlign: "center" }}>
                  <p>Không thể hiển thị PDF. Vui lòng</p>
                  <a
                    href={getFilePath(
                      selectingContract.file.replace(
                        "uploads",
                        "uploads/preview"
                      )
                    )}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    mở trong tab mới
                  </a>
                  {" hoặc "}
                  <a href={getFilePath(selectingContract.file)} download>
                    tải xuống
                  </a>
                </div>
              </object>
            </div>
          )}
        </Modal>
      )}
    </>
  );
}

export default RestaurantContract;
